import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Siren, RefreshCw, Plus, X, ExternalLink, Trash2, Edit2, Filter, Clock,
  AlertOctagon, Activity, ChevronRight, FileText, Search, CheckCircle2,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import Sparkline from '../components/Sparkline'

// ── Types ────────────────────────────────────────────────────────────────────

type Severity = 'sev1' | 'sev2' | 'sev3' | 'sev4'
type Status = 'open' | 'investigating' | 'identified' | 'monitoring' | 'resolved' | 'closed'

interface ServiceRef { id: number; name: string; tier: number | null }

interface Incident {
  id: number
  title: string
  summary: string
  severity: Severity
  severity_label: string
  status: Status
  services: number[]
  services_expanded?: ServiceRef[]
  started_at: string | null
  detected_at: string | null
  mitigated_at: string | null
  resolved_at: string | null
  duration_minutes: number | null
  detect_minutes: number | null
  root_cause: string
  impact: string
  lessons_learned: string
  postmortem_url: string | null
  owner: string
  tags: string[]
  created_at: string | null
  updated_at: string | null
}

interface Stats {
  total: number
  open: number
  by_severity: Record<string, number>
  by_status: Record<string, number>
  mttr_minutes: number | null
  mttd_minutes: number | null
  last_30d: number
  last_90d: number
  last_resolved_at: string | null
}

const SEV_BG: Record<Severity, string> = {
  sev1: 'bg-rose/10 border-rose/30 text-rose',
  sev2: 'bg-orange/10 border-orange/30 text-orange',
  sev3: 'bg-amber/10 border-amber/30 text-amber-light',
  sev4: 'bg-blue/10 border-blue/30 text-blue-light',
}

const STATUS_COLOR: Record<Status, string> = {
  open:           'text-rose',
  investigating:  'text-rose',
  identified:     'text-orange',
  monitoring:     'text-amber-light',
  resolved:       'text-emerald',
  closed:         'text-text-muted',
}

const ALL_SEVERITIES: Severity[] = ['sev1', 'sev2', 'sev3', 'sev4']
const ALL_STATUSES: Status[] = ['open', 'investigating', 'identified', 'monitoring', 'resolved', 'closed']

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatMinutes(m: number | null): string {
  if (m == null) return '—'
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  const mm = m % 60
  return mm ? `${h}h ${mm}m` : `${h}h`
}

function formatDate(s: string | null): string {
  if (!s) return '—'
  return s.slice(0, 16).replace('T', ' ')
}

function localDateTimeForInput(s: string | null): string {
  if (!s) return ''
  const d = new Date(s)
  if (isNaN(d.getTime())) return ''
  const tzOffset = d.getTimezoneOffset() * 60000
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16)
}

function inputToISO(s: string): string | null {
  if (!s) return null
  return new Date(s).toISOString()
}

// ── Form ─────────────────────────────────────────────────────────────────────

function IncidentForm({
  initial,
  serviceOptions,
  onSave,
  onCancel,
}: {
  initial?: Partial<Incident>
  serviceOptions: { id: number; name: string; tier: number }[]
  onSave: (i: any) => void
  onCancel: () => void
}) {
  const [title, setTitle] = useState(initial?.title || '')
  const [summary, setSummary] = useState(initial?.summary || '')
  const [severity, setSeverity] = useState<Severity>(initial?.severity || 'sev3')
  const [status, setStatus] = useState<Status>(initial?.status || 'open')
  const [services, setServices] = useState<number[]>(initial?.services || [])
  const [startedAt, setStartedAt] = useState(localDateTimeForInput(initial?.started_at || new Date().toISOString()))
  const [detectedAt, setDetectedAt] = useState(localDateTimeForInput(initial?.detected_at || null))
  const [mitigatedAt, setMitigatedAt] = useState(localDateTimeForInput(initial?.mitigated_at || null))
  const [resolvedAt, setResolvedAt] = useState(localDateTimeForInput(initial?.resolved_at || null))
  const [rootCause, setRootCause] = useState(initial?.root_cause || '')
  const [impact, setImpact] = useState(initial?.impact || '')
  const [lessons, setLessons] = useState(initial?.lessons_learned || '')
  const [postmortemUrl, setPostmortemUrl] = useState(initial?.postmortem_url || '')
  const [owner, setOwner] = useState(initial?.owner || '')
  const [tags, setTags] = useState((initial?.tags || []).join(', '))

  const toggleService = (id: number) => {
    setServices(services.includes(id) ? services.filter(s => s !== id) : [...services, id])
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Title *</label>
        <input value={title} onChange={e => setTitle(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="Short, descriptive — e.g. 'Checkout 5xx errors after deploy'" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Summary</label>
        <textarea value={summary} onChange={e => setSummary(e.target.value)} rows={2} className="input py-1.5 text-[12px] w-full" placeholder="One paragraph: what happened, who noticed, blast radius" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Severity</label>
          <div className="flex gap-1">
            {ALL_SEVERITIES.map(s => (
              <button type="button" key={s} onClick={() => setSeverity(s)}
                className={clsx('flex-1 py-1 text-[11px] rounded border transition-colors',
                  severity === s ? SEV_BG[s] : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
                )}>
                {s.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Status</label>
          <select value={status} onChange={e => setStatus(e.target.value as Status)} title="Status" className="input py-1.5 text-[12px] w-full">
            {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Started at</label>
          <input type="datetime-local" value={startedAt} onChange={e => setStartedAt(e.target.value)} title="Started at" className="input py-1.5 text-[12px] w-full" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Detected at</label>
          <input type="datetime-local" value={detectedAt} onChange={e => setDetectedAt(e.target.value)} title="Detected at" className="input py-1.5 text-[12px] w-full" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Mitigated at</label>
          <input type="datetime-local" value={mitigatedAt} onChange={e => setMitigatedAt(e.target.value)} title="Mitigated at" className="input py-1.5 text-[12px] w-full" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Resolved at</label>
          <input type="datetime-local" value={resolvedAt} onChange={e => setResolvedAt(e.target.value)} title="Resolved at" className="input py-1.5 text-[12px] w-full" />
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Affected services</label>
        <div className="flex flex-wrap gap-1">
          {serviceOptions.length === 0 && (
            <span className="text-[10px] text-text-muted italic">No services in catalog. Add some at /app/services.</span>
          )}
          {serviceOptions.map(s => (
            <button type="button" key={s.id} onClick={() => toggleService(s.id)}
              className={clsx('px-2 py-1 text-[10px] rounded border transition-colors',
                services.includes(s.id)
                  ? 'bg-blue/10 border-blue/30 text-blue-light'
                  : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
              )}>
              T{s.tier} {s.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Root cause</label>
        <textarea value={rootCause} onChange={e => setRootCause(e.target.value)} rows={2} className="input py-1.5 text-[12px] w-full" placeholder="What was the underlying cause?" />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Impact</label>
        <textarea value={impact} onChange={e => setImpact(e.target.value)} rows={2} className="input py-1.5 text-[12px] w-full" placeholder="Who/what was affected? Customer / revenue / data impact." />
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Lessons learned</label>
        <textarea value={lessons} onChange={e => setLessons(e.target.value)} rows={2} className="input py-1.5 text-[12px] w-full" placeholder="What did we learn? Action items go here or in linked tickets." />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Owner</label>
          <input value={owner} onChange={e => setOwner(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="incident commander" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Postmortem URL</label>
          <input value={postmortemUrl || ''} onChange={e => setPostmortemUrl(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="https://" />
        </div>
        <div className="col-span-2">
          <label className="block text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-1">Tags</label>
          <input value={tags} onChange={e => setTags(e.target.value)} className="input py-1.5 text-[12px] w-full" placeholder="deploy, db, redis" />
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button type="button"
          disabled={!title.trim() || !startedAt}
          onClick={() => onSave({
            title, summary, severity, status, services,
            started_at: inputToISO(startedAt),
            detected_at: inputToISO(detectedAt),
            mitigated_at: inputToISO(mitigatedAt),
            resolved_at: inputToISO(resolvedAt),
            root_cause: rootCause, impact, lessons_learned: lessons,
            postmortem_url: postmortemUrl || null, owner,
            tags: tags.split(',').map(t => t.trim()).filter(Boolean),
          })}
          className="btn-primary text-xs">
          Save incident
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost text-xs">Cancel</button>
      </div>
    </div>
  )
}

// ── Detail drawer ────────────────────────────────────────────────────────────

function IncidentDetail({ incident, onClose, onEdit, onDelete }: {
  incident: Incident
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-md bg-surface-card border-l border-border shadow-2xl z-40 overflow-y-auto">
      <div className="sticky top-0 bg-surface-card border-b border-border p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={clsx('px-1.5 py-0.5 text-[10px] font-semibold rounded border', SEV_BG[incident.severity])}>
            {incident.severity.toUpperCase()}
          </span>
          <span className={clsx('text-[10px] uppercase tracking-wider font-semibold capitalize', STATUS_COLOR[incident.status])}>
            {incident.status}
          </span>
        </div>
        <button type="button" onClick={onClose} title="Close" className="text-text-muted hover:text-text-primary">
          <X size={16} />
        </button>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <h2 className="text-base font-bold text-text-primary leading-snug">{incident.title}</h2>
          {incident.summary && (
            <p className="text-[11px] text-text-secondary mt-1">{incident.summary}</p>
          )}
        </div>

        {/* Timeline */}
        <div className="grid grid-cols-2 gap-3 text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted">Started</div>
            <div className="text-text-primary font-mono">{formatDate(incident.started_at)}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted">Detected</div>
            <div className="text-text-primary font-mono">{formatDate(incident.detected_at)}</div>
            {incident.detect_minutes != null && (
              <div className="text-[10px] text-text-muted">+{formatMinutes(incident.detect_minutes)}</div>
            )}
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted">Mitigated</div>
            <div className="text-text-primary font-mono">{formatDate(incident.mitigated_at)}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted">Resolved</div>
            <div className="text-text-primary font-mono">{formatDate(incident.resolved_at)}</div>
            {incident.duration_minutes != null && (
              <div className="text-[10px] text-emerald">Duration {formatMinutes(incident.duration_minutes)}</div>
            )}
          </div>
        </div>

        {incident.services_expanded && incident.services_expanded.length > 0 && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Affected services</div>
            <div className="flex flex-wrap gap-1">
              {incident.services_expanded.map(s => (
                <span key={s.id}
                  className={clsx('inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border',
                    s.tier === 1 ? 'bg-rose/10 border-rose/30 text-rose'
                      : s.tier === 2 ? 'bg-amber/10 border-amber/30 text-amber-light'
                      : 'bg-emerald/10 border-emerald/30 text-emerald'
                  )}>
                  <span className="font-mono font-semibold">T{s.tier ?? '?'}</span>
                  <span>{s.name}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {incident.root_cause && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Root cause</div>
            <p className="text-[11px] text-text-secondary whitespace-pre-wrap">{incident.root_cause}</p>
          </div>
        )}
        {incident.impact && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Impact</div>
            <p className="text-[11px] text-text-secondary whitespace-pre-wrap">{incident.impact}</p>
          </div>
        )}
        {incident.lessons_learned && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Lessons learned</div>
            <p className="text-[11px] text-text-secondary whitespace-pre-wrap">{incident.lessons_learned}</p>
          </div>
        )}

        {incident.tags.length > 0 && (
          <div>
            <div className="text-[9px] uppercase tracking-wider text-text-muted mb-1">Tags</div>
            <div className="flex flex-wrap gap-1">
              {incident.tags.map(t => (
                <span key={t} className="px-1.5 py-0.5 rounded text-[10px] bg-surface-raised text-text-secondary">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        {incident.owner && (
          <div className="text-[11px] text-text-secondary">
            <span className="text-[9px] uppercase tracking-wider text-text-muted">Owner: </span>{incident.owner}
          </div>
        )}

        {incident.postmortem_url && (
          <a href={incident.postmortem_url} target="_blank" rel="noreferrer noopener"
             className="text-[11px] text-blue-light hover:underline inline-flex items-center gap-1">
            <FileText size={11} /> Postmortem document <ExternalLink size={10} />
          </a>
        )}

        <div className="pt-3 border-t border-border flex gap-2">
          <button type="button" onClick={onEdit} className="btn-secondary text-xs">
            <Edit2 size={11} /> Edit
          </button>
          <button type="button"
            onClick={() => { if (confirm(`Delete incident "${incident.title}"?`)) onDelete() }}
            className="btn-ghost text-xs text-rose">
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function PostmortemsPage() {
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [services, setServices] = useState<{ id: number; name: string; tier: number }[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Incident | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Incident | null>(null)
  const [searchQ, setSearchQ] = useState('')
  const [sevFilter, setSevFilter] = useState<Severity | ''>('')
  const [statusFilter, setStatusFilter] = useState<Status | ''>('')

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [{ data: list }, { data: s }, { data: svc }] = await Promise.all([
        api.get('/api/postmortems'),
        api.get('/api/postmortems/stats'),
        api.get('/api/services'),
      ])
      setIncidents(list.incidents)
      setStats(s)
      setServices((svc.services || []).map((x: any) => ({ id: x.id, name: x.name, tier: x.tier })))
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const seedIfEmpty = async () => {
    if (incidents.length === 0) {
      await api.post('/api/postmortems/seed')
      fetchData()
    }
  }

  const save = async (i: any) => {
    if (editing) {
      await api.put(`/api/postmortems/${editing.id}`, i)
    } else {
      await api.post('/api/postmortems', i)
    }
    setShowForm(false); setEditing(null); setSelected(null)
    fetchData()
  }

  const remove = async (id: number) => {
    await api.delete(`/api/postmortems/${id}`)
    setSelected(null)
    fetchData()
  }

  const filtered = useMemo(() => {
    return incidents.filter(i => {
      if (sevFilter && i.severity !== sevFilter) return false
      if (statusFilter && i.status !== statusFilter) return false
      if (searchQ) {
        const q = searchQ.toLowerCase()
        const hay = `${i.title} ${i.summary} ${i.root_cause} ${i.tags.join(' ')}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [incidents, sevFilter, statusFilter, searchQ])

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }
  const isEmpty = incidents.length === 0

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <Siren size={20} className="text-rose" /> Postmortem Log
          </h1>
          <p className="text-[10px] text-text-secondary">
            Internal incident records — anchors MTTR, the "days since" countdown, and risk reviews.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isEmpty && (
            <button type="button" onClick={seedIfEmpty} className="btn-secondary text-xs" title="Insert example incidents">
              <Plus size={12} /> Seed examples
            </button>
          )}
          <button type="button" onClick={fetchData} className="btn-ghost text-xs" title="Refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
          <button type="button" onClick={() => { setEditing(null); setShowForm(true) }} className="btn-primary text-xs">
            <Plus size={12} /> Log incident
          </button>
        </div>
      </div>

      {/* Stats strip */}
      {stats && stats.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">Total</div>
            <div className="text-lg font-bold text-text-primary">{stats.total}</div>
            <div className="text-[10px] text-text-muted">{stats.last_30d} in last 30d</div>
          </div>
          <div className="card p-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">Open</div>
              <Sparkline metric="incidents_open" hours={168} width={60} height={18} tone="down-good" />
            </div>
            <div className={clsx('text-lg font-bold', stats.open > 0 ? 'text-rose' : 'text-emerald')}>{stats.open}</div>
            <div className="text-[10px] text-text-muted">not yet resolved/closed</div>
          </div>
          <div className="card p-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">MTTR</div>
              <Sparkline metric="incidents_mttr_minutes" hours={720} width={60} height={18} tone="down-good" />
            </div>
            <div className="text-lg font-bold text-blue">{formatMinutes(stats.mttr_minutes)}</div>
            <div className="text-[10px] text-text-muted">mean time to resolve</div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">MTTD</div>
            <div className="text-lg font-bold text-amber-light">{formatMinutes(stats.mttd_minutes)}</div>
            <div className="text-[10px] text-text-muted">mean time to detect</div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-text-muted">SEV1 + SEV2</div>
            <div className="text-lg font-bold text-rose">{(stats.by_severity.sev1 || 0) + (stats.by_severity.sev2 || 0)}</div>
            <div className="text-[10px] text-text-muted">major/significant</div>
          </div>
        </div>
      )}

      {/* Filters */}
      {!isEmpty && (
        <div className="card p-3 flex flex-wrap items-center gap-3">
          <Filter size={12} className="text-text-muted" />
          <div className="flex gap-1">
            <button type="button" onClick={() => setSevFilter('')}
              className={clsx('px-2 py-1 text-[10px] rounded border transition-colors',
                !sevFilter ? 'bg-blue/10 border-blue/30 text-blue-light' : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
              )}>All sev</button>
            {ALL_SEVERITIES.map(s => (
              <button type="button" key={s} onClick={() => setSevFilter(s)}
                className={clsx('px-2 py-1 text-[10px] rounded border transition-colors',
                  sevFilter === s ? SEV_BG[s] : 'bg-surface-raised border-border text-text-secondary hover:text-text-primary'
                )}>
                {s.toUpperCase()}
              </button>
            ))}
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as Status | '')} title="Status filter"
            className="input py-1 text-[11px] pr-6">
            <option value="">All statuses</option>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="flex items-center gap-1 ml-auto">
            <Search size={12} className="text-text-muted" />
            <input type="search" value={searchQ} onChange={e => setSearchQ(e.target.value)}
              placeholder="Search title / cause / tag…" className="input py-1 text-[11px] w-48" />
          </div>
        </div>
      )}

      {/* List */}
      {isEmpty ? (
        <div className="card p-10 text-center">
          <Siren size={36} className="text-text-muted mx-auto mb-3" />
          <div className="text-sm font-semibold text-text-primary mb-1">No incidents logged yet</div>
          <p className="text-[11px] text-text-secondary mb-4 max-w-sm mx-auto">
            The Postmortem Log captures internal incidents with severity, MTTR, services
            affected, and lessons learned. Resolved incidents drive the "days since" counter
            on the Countdown Board.
          </p>
          <button type="button" onClick={seedIfEmpty} className="btn-primary text-xs">
            <Plus size={12} /> Seed with two example incidents
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-surface-raised/50">
                <tr className="border-b border-border">
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Severity</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Title</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Status</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Started</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Duration</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3">Services</th>
                  <th className="text-[10px] text-text-muted font-semibold py-2 px-3 w-8" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filtered.map(i => (
                  <tr key={i.id} onClick={() => setSelected(i)}
                    className="border-b border-border/50 hover:bg-surface-raised/50 transition-colors cursor-pointer">
                    <td className="py-2 px-3">
                      <span className={clsx('px-1.5 py-0.5 rounded border text-[10px] font-semibold', SEV_BG[i.severity])}>
                        {i.severity.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="text-[11px] font-semibold text-text-primary">{i.title}</div>
                      {i.summary && (
                        <div className="text-[10px] text-text-muted truncate max-w-md" title={i.summary}>
                          {i.summary}
                        </div>
                      )}
                    </td>
                    <td className="py-2 px-3">
                      <span className={clsx('text-[10px] capitalize font-semibold', STATUS_COLOR[i.status])}>
                        {i.status}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-[10px] font-mono text-text-secondary">
                      {formatDate(i.started_at)}
                    </td>
                    <td className="py-2 px-3 text-[10px] font-mono">
                      {i.duration_minutes != null
                        ? <span className="text-emerald">{formatMinutes(i.duration_minutes)}</span>
                        : <span className="text-text-muted">ongoing</span>}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-0.5 max-w-xs">
                        {(i.services_expanded || []).slice(0, 3).map(s => (
                          <span key={s.id} className="text-[9px] px-1 py-0.5 rounded bg-surface-raised text-text-secondary">
                            T{s.tier ?? '?'} {s.name}
                          </span>
                        ))}
                        {(i.services_expanded || []).length > 3 && (
                          <span className="text-[9px] text-text-muted">+{(i.services_expanded || []).length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <ChevronRight size={12} className="text-text-muted" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="text-center py-8 text-[11px] text-text-muted">
                No incidents match these filters.
              </div>
            )}
          </div>
        </div>
      )}

      {selected && (
        <IncidentDetail
          incident={selected}
          onClose={() => setSelected(null)}
          onEdit={() => { setEditing(selected); setShowForm(true); setSelected(null) }}
          onDelete={() => remove(selected.id)}
        />
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-card border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-surface-card border-b border-border p-3 flex items-center justify-between">
              <h2 className="text-sm font-bold text-text-primary">
                {editing ? `Edit incident — ${editing.title}` : 'Log incident'}
              </h2>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null) }} title="Close" className="text-text-muted hover:text-text-primary">
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              <IncidentForm
                initial={editing || undefined}
                serviceOptions={services}
                onSave={save}
                onCancel={() => { setShowForm(false); setEditing(null) }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
